import React, { createContext, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useStartupParamsStore } from "@/store/startupParams";
import {
  RaffleInitResponse,
  RaffleParticipant,
  RaffleResults,
} from "@/apps/raffle/types";
import { useRaffleData } from "./useRaffleData";
import { getCurrentParticipantIndex } from "./utils";

type RaffleResultsAnimationState = {
  activeParticipantIndex?: number;
  rotation: number;
};

type LoadedRafflePageData = {
  raffle: RaffleInitResponse;
  participants: RaffleParticipant[];
  results: RaffleResults | null;
  resultsSeconds: number | null;
  currentParticipantIndex?: number;
  isParticipationComplete: boolean;
};

type LoadedRafflePageContextValue = LoadedRafflePageData & {
  resultsAnimation: RaffleResultsAnimationState;
  setResultsAnimation: (animation: RaffleResultsAnimationState) => void;
};

type RafflePageData = {
  isLoading: boolean;
  isEmpty: boolean;
  raffleData: LoadedRafflePageData | null;
};

const RafflePageContext = createContext<LoadedRafflePageContextValue | null>(
  null
);

export const useRafflePageData = (): RafflePageData => {
  const { bonusId } = useParams();
  const yandexId = useStartupParamsStore((state) => state.auth.yandexId);
  const {
    raffle,
    participants,
    results,
    resultsSeconds,
    isLoading,
    isEmpty,
  } = useRaffleData(yandexId, bonusId);

  const raffleData = useMemo<LoadedRafflePageData | null>(() => {
    if (!raffle) return null;

    const { conditions } = raffle.me;
    const isRaffleFinished = Boolean(raffle.drawn_at);
    const hasMetParticipationConditions = Boolean(
      conditions?.has_goal_this_week &&
        conditions?.has_completed_order_this_week
    );

    return {
      raffle,
      participants,
      results,
      resultsSeconds,
      currentParticipantIndex: getCurrentParticipantIndex(raffle, participants),
      isParticipationComplete:
        isRaffleFinished || hasMetParticipationConditions,
    };
  }, [participants, raffle, results, resultsSeconds]);

  return {
    isLoading,
    isEmpty,
    raffleData,
  };
};

type RafflePageProviderProps = {
  raffleData: LoadedRafflePageData;
  children: React.ReactNode;
};

export const RafflePageProvider = ({
  raffleData,
  children,
}: RafflePageProviderProps) => {
  const [resultsAnimation, setResultsAnimation] =
    useState<RaffleResultsAnimationState>({
      rotation: 0,
    });

  const value = useMemo<LoadedRafflePageContextValue>(
    () => ({
      ...raffleData,
      resultsAnimation,
      setResultsAnimation,
    }),
    [raffleData, resultsAnimation]
  );

  return (
    <RafflePageContext.Provider value={value}>
      {children}
    </RafflePageContext.Provider>
  );
};

export const useRafflePageContext = () => {
  const context = useContext(RafflePageContext);

  if (!context) {
    throw new Error("useRafflePage must be used within RafflePageProvider");
  }

  return context;
};
